/* global axios */
import { DataManager } from '../helper/CacheHelper/DataManager';
import ApiClient from './ApiClient';

// DDukDoc: 앱 부팅 때 캐시 사용 API(라벨·받은편지함·팀 등)가 각자 cache_keys 를 불러
// 같은 요청이 3~4번 동시에 나갔다(2026-09-04 실측). 짧은 창 안의 동시 호출은 한 번만 보낸다.
const CACHE_KEYS_SHARE_WINDOW_MS = 1000;
const inflightCacheKeys = new Map();

function fetchCacheKeys(accountId) {
  const now = Date.now();
  const entry = inflightCacheKeys.get(accountId);
  if (entry && now - entry.startedAt < CACHE_KEYS_SHARE_WINDOW_MS) {
    return entry.promise;
  }
  const promise = axios
    .get(`/api/v1/accounts/${accountId}/cache_keys`)
    .then(({ data }) => data.cache_keys);
  inflightCacheKeys.set(accountId, { promise, startedAt: now });
  promise.finally(() => {
    if (inflightCacheKeys.get(accountId)?.promise === promise) {
      inflightCacheKeys.delete(accountId);
    }
  });
  return promise;
}

class CacheEnabledApiClient extends ApiClient {
  constructor(resource, options = {}) {
    super(resource, options);
    this.dataManager = new DataManager(this.accountIdFromRoute);
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    throw new Error('cacheModelName is not defined');
  }

  get(cache = false) {
    if (cache) {
      return this.getFromCache();
    }

    return this.getFromNetwork();
  }

  getFromNetwork() {
    return axios.get(this.url);
  }

  // eslint-disable-next-line class-methods-use-this
  extractDataFromResponse(response) {
    return response.data.payload;
  }

  // eslint-disable-next-line class-methods-use-this
  marshallData(dataToParse) {
    return { data: { payload: dataToParse } };
  }

  async getFromCache() {
    try {
      // IDB is not supported in Firefox private mode: https://bugzilla.mozilla.org/show_bug.cgi?id=781982
      await this.dataManager.initDb();
    } catch {
      return this.getFromNetwork();
    }

    const cacheKeys = await fetchCacheKeys(this.accountIdFromRoute);
    const cacheKeyFromApi = cacheKeys[this.cacheModelName];
    const isCacheValid = await this.validateCacheKey(cacheKeyFromApi);

    let localData = [];
    if (isCacheValid) {
      localData = await this.dataManager.get({
        modelName: this.cacheModelName,
      });
    }

    if (localData.length === 0) {
      return this.refetchAndCommit(cacheKeyFromApi);
    }

    return this.marshallData(localData);
  }

  async refetchAndCommit(newKey = null) {
    const response = await this.getFromNetwork();

    try {
      await this.dataManager.initDb();

      this.dataManager.replace({
        modelName: this.cacheModelName,
        data: this.extractDataFromResponse(response),
      });

      await this.dataManager.setCacheKeys({
        [this.cacheModelName]: newKey,
      });
    } catch {
      // Ignore error
    }

    return response;
  }

  async validateCacheKey(cacheKeyFromApi) {
    if (!this.dataManager.db) {
      await this.dataManager.initDb();
    }

    const cachekey = await this.dataManager.getCacheKey(this.cacheModelName);
    return cacheKeyFromApi === cachekey;
  }
}

export default CacheEnabledApiClient;
